import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { logger } from "../lib/logger";

const REMEMBER_EMAIL_KEY = "noor:remember_email";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Au montage : récupérer l'email enregistré la dernière fois (si "remember" était coché)
  useEffect(() => {
    try {
      const saved = localStorage.getItem(REMEMBER_EMAIL_KEY);
      if (saved) {
        setEmail(saved);
        setRemember(true);
      }
    } catch (e) {
      logger.debug("localStorage unavailable:", e);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const u = await login(email, password, remember);
      // Persistance de l'email selon la case
      try {
        if (remember) {
          localStorage.setItem(REMEMBER_EMAIL_KEY, email);
        } else {
          localStorage.removeItem(REMEMBER_EMAIL_KEY);
        }
      } catch (storageErr) {
        logger.debug("localStorage write failed:", storageErr);
      }
      toast.success(`Bienvenue, ${u.name}`);
      // Redirection : si ?next=... fourni et compatible avec le rôle, l'utiliser
      if (next) {
        // n'autoriser que des chemins internes commençant par "/"
        if (next.startsWith("/") && !next.startsWith("//")) {
          navigate(next);
          return;
        }
      }
      navigate(u.role === "admin" ? "/admin" : u.role === "parent" ? "/parent" : "/enseignant");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16 fade-in" data-testid="login-page">
      <h1 className="font-[Outfit] font-semibold text-3xl md:text-4xl tracking-tight text-[#1A2547]">Connexion</h1>
      <p className="text-[#5C6861] mt-2">Espace réservé aux enseignants et à l'administration.</p>

      <form onSubmit={submit} className="mt-10 space-y-5 bg-white border border-[#EAE6DF] rounded-2xl p-6 md:p-8">
        <div>
          <Label htmlFor="email" className="text-sm">Email</Label>
          <Input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5 rounded-xl"
            data-testid="login-email"
          />
        </div>
        <div>
          <Label htmlFor="password" className="text-sm">Mot de passe</Label>
          <Input
            id="password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1.5 rounded-xl"
            data-testid="login-password"
          />
        </div>
        <label className="flex items-center gap-3 cursor-pointer select-none py-1">
          <Checkbox
            checked={remember}
            onCheckedChange={(v) => setRemember(!!v)}
            className="w-5 h-5"
            data-testid="login-remember"
          />
          <span className="text-sm text-[#1A2547]">Se souvenir de moi</span>
          <span className="ml-auto text-[10px] text-[#8a92a3]">
            {remember ? "Session de 30 jours" : "Session de 1 jour"}
          </span>
        </label>
        <div className="flex justify-end -mt-1">
          <ForgotPasswordDialog defaultEmail={email} />
        </div>
        {err && <div className="text-sm text-[#B0413E]" data-testid="login-error">{err}</div>}
        <Button
          type="submit"
          disabled={loading}
          className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#14265E] text-white h-11"
          data-testid="login-submit"
        >
          {loading ? "Connexion…" : "Se connecter"}
        </Button>
      </form>
    </div>
  );
}

function ForgotPasswordDialog({ defaultEmail }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(defaultEmail || "");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { if (open) { setEmail(defaultEmail || ""); setDone(false); } }, [open, defaultEmail]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setDone(true);
    } catch {
      // On garde un message générique pour ne pas révéler si l'email existe
      setDone(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" className="text-xs text-[#1E3A8A] hover:underline" data-testid="forgot-password-link">
          Mot de passe oublié ?
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Réinitialiser mon mot de passe</DialogTitle>
        </DialogHeader>
        {done ? (
          <div className="space-y-3" data-testid="forgot-password-done">
            <DialogDescription className="text-sm text-[#1A2547]">
              Si l'adresse <strong>{email || "fournie"}</strong> correspond à un compte, un email contenant un mot de passe temporaire vient d'être envoyé.
            </DialogDescription>
            <p className="text-xs text-[#5c6374]">
              Pensez à vérifier vos spams. Vous devrez changer ce mot de passe à votre première connexion.
            </p>
            <Button type="button" onClick={() => setOpen(false)} className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid="forgot-password-close">
              Fermer
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <DialogDescription className="text-xs">
              Saisissez l'adresse email associée à votre compte. Un mot de passe temporaire vous sera envoyé.
            </DialogDescription>
            <div>
              <Label htmlFor="forgot-email" className="text-sm">Email</Label>
              <Input
                id="forgot-email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 rounded-xl"
                data-testid="forgot-password-email"
                placeholder="prenom.nom@exemple.fr"
              />
            </div>
            <Button type="submit" disabled={sending} className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid="forgot-password-submit">
              {sending ? "Envoi…" : "Envoyer le mot de passe temporaire"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
